import React, { useEffect, useState, useRef, useCallback } from 'react';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-fresh.css';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { array, bool, func, object, number } from 'prop-types';
import { createUniqueId } from '../../utils/common-util.js';
import {
   createCellEditStartHandler,
   createCellValueChangedHandler,
   createDragToSelectHandlers,
   createFirstDataRenderedHandler,
   createRowClickHandler,
   getContextMenuItems,
   handleKeyboardNavigation,
   handleKeyboardShortcuts,
   navigateToNextCell,
   pasteClipboardData,
} from './__helpers__/functions';
import MyCustomHeader from './custom-header.jsx';

LicenseManager.setLicenseKey(process.env.AG_GRID_ENTERPRISE_LICENSE_KEY);

const AgGridReactTable = ({
   rowData: rowDataFromParent,
   headers,
   onSelectedRowsChange,
   onSelectionValueChanged,
   getGridApiRef,
   handleCallBack,
   rowHeight,
   enableMultiRowSelection = false,
   resetTableOnRerender = false,
   autoSizeColumns = true,
   enableAllColumnResizable = true,
   enableAllColumnSort = true,
   enableAllColumnFilter = true,
   selectFirstRowByDefault = true,
   rowClassRules = {},
   enableRowIds = false, // when true, rowData must have unique id
   rememberSelectedRow = false,
   enableContextMenu = true,
   contextMenuConfig = {},
   appendIncomingRowData = false, // when true, updated (paginated) rowData will be appended to existing data, edited states will be retained
   onModelUpdated = () => {}, // callback to handle model updates, e.g., for adding or deleting rows
}) => {
   const [rowData, setRowData] = useState([]);
   const [selectedRow, setSelectedRow] = useState(null);
   const gridApiRef = useRef(null);
   const isDraggingRef = useRef(false);
   const startRowRef = useRef(null);
   const lastRowClickedRef = useRef(null);
   const hostCarrierCode = sessionStorage.getItem('carrierCode') ?? null;

   const onGridReady = (params) => {
      gridApiRef.current = params.api;
      getGridApiRef?.(gridApiRef);
   };

   const updateSelectedRows = useCallback(() => {
      if (!gridApiRef?.current) return;
      const selectedNodes = gridApiRef?.current?.getSelectedNodes();
      onSelectedRowsChange?.(selectedNodes);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);
   // dependency is received from props, excluding it here to avoid creating a callback 'onSelectedRowsChange' function in the parent every time

   const { onCellMouseDown, onCellMouseOver } = createDragToSelectHandlers({
      isDraggingRef,
      startRowRef,
      updateSelectedRows,
      gridApiRef: gridApiRef.current,
   });

   useEffect(() => {
      if (gridApiRef?.current && appendIncomingRowData) {
         gridApiRef.current?.applyTransaction({
            add: rowDataFromParent,
         });
      } else {
         setTimeout(() => {
            setRowData(rowDataFromParent);
         }, 0);
      }
   }, [rowDataFromParent, appendIncomingRowData]);

   useEffect(() => {
      if (selectFirstRowByDefault) {
         if (rememberSelectedRow && selectedRow) return;

         const firstRowNode = gridApiRef?.current?.getDisplayedRowAtIndex(0);
         firstRowNode?.setSelected(true);
         setSelectedRow(firstRowNode);
         lastRowClickedRef.current = firstRowNode?.rowIndex;
         updateSelectedRows();
      }
   }, [
      rowData,
      selectFirstRowByDefault,
      rememberSelectedRow,
      selectedRow,
      updateSelectedRows,
   ]);

   useEffect(() => {
      const handleKeyDown = (event) =>
         handleKeyboardShortcuts(event, gridApiRef, contextMenuConfig);

      window.addEventListener('keydown', handleKeyDown);
      return () => {
         window.removeEventListener('keydown', handleKeyDown);
      };
   }, [contextMenuConfig]);

   const gridOptions = {
      onGridReady,
      rowClassRules: {
         'ag-row-white host-carrier': ({ data }) =>
            data.carrier_code === hostCarrierCode ||
            data.carrierCode === hostCarrierCode,
         'ag-new-row': (params) => params.data.isNewRow,
         'ag-edited-row': (params) => params.data.isEdited,
         'ag-error-row': (params) => params.data.isError,
         ...rowClassRules,
      },
      onCellMouseDown,
      onCellMouseOver,
      tooltipShowDelay: 0,
      undoRedoCellEditing: true,
      suppressClipboardPaste: true,
      suppressScrollOnNewData: true,
      suppressColumnVirtualisation: true,
      suppressRowClickSelection: true,
      stopEditingWhenCellsLoseFocus: true,
      context: handleCallBack,
      defaultColDef: {
         resizable: enableAllColumnResizable,
         filter: enableAllColumnFilter,
         sortable: enableAllColumnSort,
         unSortIcon: enableAllColumnSort,
         minWidth: 50,
         headerComponent: MyCustomHeader,
         menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
      },
      rowSelection: enableMultiRowSelection ? 'multiple' : 'single',
      onRowClicked: createRowClickHandler(
         updateSelectedRows,
         lastRowClickedRef
      ),
      onFirstDataRendered: createFirstDataRenderedHandler(autoSizeColumns),
      onCellKeyDown: handleKeyboardNavigation(updateSelectedRows),
      onCellValueChanged: createCellValueChangedHandler(
         onSelectionValueChanged,
         gridApiRef
      ),
      ...(enableContextMenu && {
         getContextMenuItems: () =>
            getContextMenuItems(gridApiRef, contextMenuConfig),
      }),
      ...(enableRowIds && { getRowId: (params) => params.data.id }),
      ...(resetTableOnRerender && { key: createUniqueId() }),
      navigateToNextCell,
      rowHeight: rowHeight || 17,
      animateRows: true,
      onCellEditingStarted: createCellEditStartHandler(gridApiRef),
      onModelUpdated: onModelUpdated,
   };

   return (
      <section
         className='ag-grid-react-table ag-theme-fresh'
         onPaste={() => pasteClipboardData(gridApiRef)}
         onContextMenu={(event) => event.preventDefault()}
      >
         <AgGridReact rowData={rowData} columnDefs={headers} {...gridOptions} />
      </section>
   );
};

AgGridReactTable.propTypes = {
   rowData: array,
   headers: array,
   rowHeight: number,
   onSelectedRowsChange: func,
   onSelectionValueChanged: func,
   getGridApiRef: func,
   handleCallBack: func,
   enableUniqueRows: bool,
   enableMultiRowSelection: bool,
   resetTableOnRerender: bool,
   autoSizeColumns: bool,
   enableAllColumnResizable: bool,
   enableAllColumnSort: bool,
   enableAllColumnFilter: bool,
   selectFirstRowByDefault: bool,
   rowClassRules: object,
   enableRowIds: bool,
   rememberSelectedRow: bool,
   enableContextMenu: bool,
   contextMenuConfig: object,
   appendIncomingRowData: bool,
   onModelUpdated: func,
};

export default AgGridReactTable;
